import { Link } from 'react-router-dom';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import SEO from '../components/common/SEO';
import Button from '../components/common/Button';
import BookGrid from '../components/books/BookGrid';
import EventGrid from '../components/events/EventGrid';
import GroupGrid from '../components/groups/GroupGrid';
import CountUp from '../components/common/CountUp';
import { useBooks } from '../hooks/useBooks';
import { useEvents } from '../hooks/useEvents';
import { useGroups } from '../hooks/useGroups';
import { Leaf, Users, BookOpen } from 'lucide-react';
import { BookOpenIcon, UsersIcon, CalendarIcon } from '../components/common/Icons';
import { getFadeUpVariants, getStaggerContainer } from '../utils/motionVariants';
import { useReducedMotion } from '../hooks/useReducedMotion';

const PROOF_ITEMS = ['romans', 'sciences', 'histoire', 'dev_perso', 'ecologie', 'litt_afrique', 'art', 'jeunesse'];

const Home = () => {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();
  const booksRef = useRef(null);
  const heroRef = useRef(null);
  const booksInView = useInView(booksRef, { once: true, amount: 0.1 });
  
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 1000], [0, 200]);
  const y2 = useTransform(scrollY, [0, 1000], [0, -150]);

  const { books, loading } = useBooks();
  const { events, loading: eventsLoading } = useEvents();
  const { groups, loading: groupsLoading } = useGroups();

  const featuredBooks = books.slice(0, 4);
  const featuredEvents = events.slice(0, 3);
  const featuredGroups = groups.slice(0, 3).map((g) => ({ ...g, image_couverture: g.image || g.image_couverture }));

  const stats = [
    { icon: BookOpenIcon, label: t('home.stats.books'), value: '500', suffix: '+' },
    { icon: UsersIcon, label: t('home.stats.groups'), value: '10', suffix: '+' },
    { icon: CalendarIcon, label: t('home.stats.events'), value: '10', suffix: '+' },
    { icon: UsersIcon, label: t('home.stats.members'), value: '200', suffix: '+' },
  ];

  return (
    <div className="bg-surface-main dark:bg-dark-bg">
      <SEO
        title="PROTEGE QV Lecture+ | Bibliothèque Communautaire à Yaoundé"
        description="Découvrez la bibliothèque communautaire de PROTEGE QV à Yaoundé. Plus de 1 500 livres disponibles, clubs de lecture, ateliers culturels."
        keywords="bibliothèque Yaoundé, livres Cameroun, club lecture, PROTEGE QV, éducation, culture"
      />

      {/* Hero Section */}
      <section ref={heroRef} className="relative bg-[#FAFAFA] dark:bg-dark-bg py-16 md:py-24 overflow-hidden min-h-[85vh] flex items-center">
        {/* Parallax Background Shapes */}
        <motion.div style={{ y: y1 }} className="absolute inset-0 opacity-[0.03] dark:opacity-[0.02] pointer-events-none">
          <svg className="absolute bottom-10 left-10 w-96 h-96 text-primary-500" viewBox="0 0 100 100" fill="currentColor">
            <path d="M50 10 Q70 30 60 50 Q50 70 30 60 Q10 50 20 30 Q30 10 50 10" />
          </svg>
        </motion.div>
        <motion.div style={{ y: y2 }} className="absolute inset-0 opacity-[0.04] dark:opacity-[0.03] pointer-events-none">
          <svg className="absolute top-10 right-10 w-72 h-72 text-emerald-500" viewBox="0 0 100 100" fill="currentColor">
            <path d="M40 20 Q60 10 80 30 Q90 50 70 70 Q50 90 30 70 Q10 50 20 30 Q30 10 40 20" />
          </svg>
        </motion.div>
        
        {/* Floating blurred orbs */}
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-primary-400/10 rounded-full blur-3xl pointer-events-none mix-blend-multiply dark:mix-blend-screen" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-emerald-400/10 rounded-full blur-3xl pointer-events-none mix-blend-multiply dark:mix-blend-screen" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10">
          <div className="grid grid-cols-1 lg:grid-cols-[55%_45%] gap-12 items-center">
            <div className="order-2 lg:order-1">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, type: 'spring' }}
                className="inline-flex items-center px-4 py-2 rounded-full bg-white/80 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 text-sm font-semibold mb-6 shadow-sm border border-primary-100 dark:border-primary-800 backdrop-blur-md"
              >
                <span className="w-2 h-2 rounded-full bg-emerald-500 mr-2 animate-pulse" />
                {t('home.hero.badge')}
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.6, ease: 'easeOut' }}
                className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 dark:text-white mb-6 leading-[1.15]"
              >
                {t('home.hero.welcome')}
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.6, ease: 'easeOut' }}
                className="text-lg md:text-xl text-gray-600 dark:text-gray-300 mb-8 leading-relaxed max-w-2xl"
              >
                {t('home.hero.subtitle')}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.5 }}
                className="flex flex-col sm:flex-row gap-4"
              >
                <Link to="/books">
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Button size="lg" className="w-full sm:w-auto shadow-lg shadow-primary-500/20">{t('home.services.catalog.link')} →</Button>
                  </motion.div>
                </Link>
                <Link to="/groups">
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Button size="lg" variant="outline" className="w-full sm:w-auto bg-white/50 dark:bg-transparent backdrop-blur-sm">
                      {t('home.featured_groups.view_all')} →
                    </Button>
                  </motion.div>
                </Link>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5, duration: 0.6, type: 'spring' }}
              className="order-1 lg:order-2"
            >
              <div className="grid grid-cols-2 gap-4">
                {stats.map((stat, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 + i * 0.1, duration: 0.4 }}
                    whileHover={{ scale: 1.05, y: -5 }}
                    className="bg-white/80 dark:bg-dark-surface/80 backdrop-blur-md rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-800 transition-colors hover:border-primary-200 dark:hover:border-primary-700"
                  >
                    <stat.icon className="h-8 w-8 text-primary-500 mb-3" />
                    <div className="text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight">
                      <CountUp end={stat.value} suffix={stat.suffix} />
                    </div>
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400 mt-1">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
              
              {featuredBooks[0] && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.9, duration: 0.5 }}
                  className="mt-6"
                >
                  <Link to={`/books/${featuredBooks[0].id}`} className="block outline-none">
                    <motion.div 
                      whileHover={{ y: -8, scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="bg-white dark:bg-dark-surface rounded-2xl shadow-xl overflow-hidden border border-gray-100 dark:border-gray-800 group"
                    >
                      <div className="aspect-[2/3] overflow-hidden relative">
                        <img
                          src={featuredBooks[0].image || featuredBooks[0].cover_url || featuredBooks[0].image_couverture}
                          alt={featuredBooks[0].title || featuredBooks[0].titre}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                          width="400"
                          height="600"
                          loading="eager"
                          fetchPriority="high"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      </div>
                      <div className="p-5">
                        <h3 className="font-semibold text-gray-900 dark:text-white line-clamp-1 text-lg mb-1 group-hover:text-primary-500 transition-colors">
                          {featuredBooks[0].title || featuredBooks[0].titre}
                        </h3>
                        <div className="flex items-center text-sm font-medium text-primary-600 dark:text-primary-400">
                          {t('home.featured_books.view_all')}
                          <motion.span 
                            initial={{ x: 0 }} 
                            whileHover={{ x: 5 }}
                            className="ml-2"
                          >→</motion.span>
                        </div>
                      </div>
                    </motion.div>
                  </Link>
                </motion.div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Proof Social Scroll */}
      <section className="py-8 bg-primary-50 dark:bg-primary-900/10 overflow-hidden border-y border-primary-100 dark:border-primary-800/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-4 text-center">
          <p className="text-xs font-semibold text-primary-400 uppercase tracking-widest">{t('home.proof_scroll.title', 'Découvrez nos thématiques')}</p>
        </div>
        <div
          className="proof-scroll flex gap-16 whitespace-nowrap text-sm font-medium text-gray-500 dark:text-gray-400"
          style={{
            animation: 'scroll-horizontal 30s linear infinite',
            maskImage: 'linear-gradient(90deg, transparent, black 15%, black 85%, transparent)',
            WebkitMaskImage: 'linear-gradient(90deg, transparent, black 15%, black 85%, transparent)',
          }}
        >
          {[...PROOF_ITEMS, ...PROOF_ITEMS, ...PROOF_ITEMS].map((key, i) => (
            <motion.span 
              key={i} 
              whileHover={{ scale: 1.1, color: '#10b981' }}
              className="inline-flex items-center gap-3 cursor-pointer transition-colors"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-primary-400/50" />
              {t(`home.proof_scroll.${key}`)}
            </motion.span>
          ))}
        </div>
      </section>

      <style>{`
        @keyframes scroll-horizontal {
          0% { transform: translateX(0); }
          100% { transform: translateX(-33.33%); }
        }
        .proof-scroll:hover { animation-play-state: paused; }
      `}</style>

      {/* Featured Books */}
      <section ref={booksRef} className="py-16 md:py-24 bg-white dark:bg-dark-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={getStaggerContainer(reduceMotion)}
            initial="hidden"
            animate={booksInView ? 'visible' : 'hidden'}
            className="text-center mb-12"
          >
            <motion.h2 variants={getFadeUpVariants(reduceMotion)} className="text-3xl font-bold text-gray-900 dark:text-white mb-4 font-display">
              {t('home.featured_books.title')}
            </motion.h2>
            <motion.p variants={getFadeUpVariants(reduceMotion)} className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              {t('home.featured_books.subtitle')}
            </motion.p>
          </motion.div>

          <BookGrid books={featuredBooks} loading={loading} itemsPerPage={4} />

          <div className="text-center mt-8">
            <Link to="/books">
              <Button variant="tertiary" size="lg">
                {t('home.featured_books.view_all')} →
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Groups Section */}
      <section className="py-16 md:py-24 bg-primary-50 dark:bg-primary-900/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4 font-display">
              {t('home.featured_groups.title')}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              {t('home.featured_groups.subtitle')}
            </p>
          </div>

          <GroupGrid groups={featuredGroups} loading={groupsLoading} itemsPerPage={3} />

          <div className="text-center mt-8">
            <Link to="/groups">
              <Button variant="outline" size="lg">
                {t('home.featured_groups.view_all')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Events Section */}
      <section className="py-16 md:py-24 bg-white dark:bg-dark-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4 font-display">
              {t('home.featured_events.title')}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              {t('home.featured_events.subtitle')}
            </p>
          </div>

          <EventGrid events={featuredEvents} loading={eventsLoading} itemsPerPage={3} />

          <div className="text-center mt-8">
            <Link to="/events">
              <Button variant="tertiary" size="lg">
                {t('home.featured_events.view_all')} →
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 md:py-24 bg-primary-dark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4 font-display">
              {t('home.mission.title')}
            </h2>
            <p className="text-primary-100 max-w-3xl mx-auto text-lg">
              {t('home.mission.subtitle')}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Leaf, key: 'ecologie' },
              { icon: Users, key: 'communaute' },
              { icon: BookOpen, key: 'savoir' },
            ].map((item, i) => (
              <motion.div
                key={item.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/10 mb-4">
                  <item.icon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white">
                  {t(`home.mission.${item.key}`)}
                </h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats CountUp */}
      <section className="py-16 bg-white dark:bg-dark-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <div className="p-4 bg-primary-50 dark:bg-primary-900/20 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                  <stat.icon className="h-10 w-10 text-primary-600 dark:text-primary-400" />
                </div>
                <div className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                  <CountUp end={stat.value} suffix={stat.suffix} duration={2000} />
                </div>
                <div className="text-gray-600 dark:text-gray-400 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-16 md:py-24 bg-primary-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4 font-display">
            {t('home.cta.title')}
          </h2>
          <p className="text-primary-100 mb-6 max-w-2xl mx-auto">
            {t('home.cta.subtitle')}
          </p>
          <Link to="/books" className="inline-block mb-6">
            <Button size="lg" className="bg-white text-primary-600 hover:bg-primary-50">
              {t('home.cta.btn')}
            </Button>
          </Link>
          <p className="text-primary-100 text-sm">
            {t('home.cta.ai_assistant')}
          </p>
        </div>
      </section>
    </div>
  );
};

export default Home;
